import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AccountType,
  PayoutBatchStatus,
  PayoutItemStatus,
  type PayoutBatchDto,
  type PayoutItemDto,
} from '@paladin-monorepo/common';
import { clientPayoutBatchesApi } from '@/lib/api/client-payout-batches';
import { Loading } from '@/components/Loading';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatDate';
import { useAuth } from '@/lib/auth';
import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { getAppFormatLocale } from '@/utils/locale';

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat(getAppFormatLocale(), { style: 'currency', currency }).format(amount);
}

const batchStatusVariant: Partial<
  Record<PayoutBatchStatus, 'default' | 'secondary' | 'warning' | 'success' | 'destructive'>
> = {
  [PayoutBatchStatus.DRAFT]: 'secondary',
  [PayoutBatchStatus.PENDING_FIRST_APPROVAL]: 'warning',
  [PayoutBatchStatus.PENDING_SECOND_APPROVAL]: 'warning',
  [PayoutBatchStatus.APPROVED]: 'success',
  [PayoutBatchStatus.ABO_EXPORTED]: 'default',
};

export function ClientPayoutBatchesSettingsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ClientPayoutBatchesSettingsTab' });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const isInquirer = user?.account?.accountType === AccountType.Inquirer;

  const listQuery = useQuery({
    queryKey: ['client-payout-batches'],
    queryFn: () => clientPayoutBatchesApi.list(),
    enabled: isInquirer,
  });

  const detailQuery = useQuery({
    queryKey: ['client-payout-batches', selectedId],
    queryFn: () => clientPayoutBatchesApi.get(selectedId!),
    enabled: Boolean(selectedId) && isInquirer,
  });

  const createMutation = useMutation({
    mutationFn: () => clientPayoutBatchesApi.create({ currency: 'CZK' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client-payout-batches'] });
    },
  });

  const patchItemsMutation = useMutation({
    mutationFn: ({ id, excluded }: { id: string; excluded: string[] }) =>
      clientPayoutBatchesApi.updateItems(id, { excludedPayoutItemIds: excluded }),
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: ['client-payout-batches'] });
      void qc.invalidateQueries({ queryKey: ['client-payout-batches', v.id] });
    },
  });

  const submitMutation = useMutation({
    mutationFn: (id: string) => clientPayoutBatchesApi.submitForApproval(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['client-payout-batches'] });
      void qc.invalidateQueries({ queryKey: ['client-payout-batches', id] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => clientPayoutBatchesApi.approve(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['client-payout-batches'] });
      void qc.invalidateQueries({ queryKey: ['client-payout-batches', id] });
    },
  });

  const exportMutation = useMutation({
    mutationFn: (id: string) => clientPayoutBatchesApi.exportAbo(id),
  });

  const detail = detailQuery.data;

  const setExcluded = useCallback(
    (itemId: string, excluded: boolean) => {
      if (!detail) return;
      const next = new Set(
        detail.items.filter((i) => i.status === PayoutItemStatus.EXCLUDED).map((i) => i.id),
      );
      if (excluded) next.add(itemId);
      else next.delete(itemId);
      patchItemsMutation.mutate({ id: detail.id, excluded: [...next] });
    },
    [detail, patchItemsMutation],
  );

  if (!isInquirer) {
    return <p className="text-sm text-muted-foreground">{t('settings.payoutBatches.notInquirer')}</p>;
  }

  if (listQuery.isLoading) {
    return <Loading />;
  }

  if (listQuery.isError) {
    return <p className="text-sm text-destructive">{t('settings.payoutBatches.error')}</p>;
  }

  const batches = listQuery.data ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">{t('settings.payoutBatches.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('settings.payoutBatches.description')}</p>
        </div>
        <Button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
        >
          {t('settings.payoutBatches.newDraft')}
        </Button>
      </div>

      <div>
        <h3 className="text-sm font-medium mb-2">{t('settings.payoutBatches.listTitle')}</h3>
        {batches.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('settings.payoutBatches.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.payoutBatches.columns.id')}</TableHead>
                <TableHead>{t('settings.payoutBatches.columns.status')}</TableHead>
                <TableHead className="text-right">{t('settings.payoutBatches.columns.includedTotal')}</TableHead>
                <TableHead>{t('settings.payoutBatches.columns.created')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b: PayoutBatchDto) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">{b.id}</TableCell>
                  <TableCell>
                    <Badge variant={batchStatusVariant[b.status] ?? 'secondary'}>{b.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatAmount(b.totalNetIncluded, b.currency)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(b.createdAt)}</TableCell>
                  <TableCell>
                    <Button type="button" variant="outline" size="sm" onClick={() => setSelectedId(b.id)}>
                      {t('settings.payoutBatches.open')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {selectedId && detailQuery.isLoading && <Loading />}
      {selectedId && detail && (
        <div className="space-y-4 border rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">{t('settings.payoutBatches.detailTitle')}</h3>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
              {t('settings.payoutBatches.closeDetail')}
            </Button>
          </div>

          <div className="text-sm text-muted-foreground flex flex-wrap gap-4">
            <span>
              {t('settings.payoutBatches.included')}:{' '}
              <strong className="text-foreground">{formatAmount(detail.totalNetIncluded, detail.currency)}</strong>
            </span>
            <span>
              {t('settings.payoutBatches.excluded')}:{' '}
              <strong className="text-foreground">{formatAmount(detail.totalNetExcluded, detail.currency)}</strong>
            </span>
          </div>

          {detail.approvals.length > 0 && (
            <ul className="text-sm text-muted-foreground list-disc pl-5">
              {detail.approvals.map((a) => (
                <li key={`${a.step}-${a.userId}-${a.createdAt}`}>
                  {t('settings.payoutBatches.approvalLine', { step: a.step, userId: a.userId })}
                </li>
              ))}
            </ul>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">{t('settings.payoutBatches.columns.include')}</TableHead>
                <TableHead>{t('settings.payoutBatches.columns.project')}</TableHead>
                <TableHead className="text-right">{t('settings.payoutBatches.columns.net')}</TableHead>
                <TableHead>{t('settings.payoutBatches.columns.creditor')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.items.map((row: PayoutItemDto) => {
                const included = row.status !== PayoutItemStatus.EXCLUDED;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        checked={included}
                        disabled={detail.status !== PayoutBatchStatus.DRAFT || patchItemsMutation.isPending}
                        onCheckedChange={(v) => setExcluded(row.id, v !== true)}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/projects/${encodeURIComponent(row.projectId)}/workspace/payments`}
                        className="text-primary hover:underline"
                      >
                        {row.projectTitle || row.projectId}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatAmount(row.amountNet, row.currency)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.creditorName ?? '-'}
                      {row.creditorIban ? ` · ${row.creditorIban}` : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex flex-wrap gap-2">
            {detail.status === PayoutBatchStatus.DRAFT && (
              <Button
                type="button"
                onClick={() => submitMutation.mutate(detail.id)}
                disabled={submitMutation.isPending || detail.includedItemCount === 0}
              >
                {t('settings.payoutBatches.submitForApproval')}
              </Button>
            )}
            {(detail.status === PayoutBatchStatus.PENDING_FIRST_APPROVAL ||
              detail.status === PayoutBatchStatus.PENDING_SECOND_APPROVAL) && (
              <Button
                type="button"
                onClick={() => approveMutation.mutate(detail.id)}
                disabled={approveMutation.isPending}
              >
                {t('settings.payoutBatches.approve')}
              </Button>
            )}
            {detail.status === PayoutBatchStatus.APPROVED && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => exportMutation.mutate(detail.id)}
                disabled={exportMutation.isPending}
              >
                {t('settings.payoutBatches.exportAbo')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
